import './Header.css';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import useScroll from 'Hooks/useScroll';
import { Logo } from 'Components/Logo';
import { SmartLink } from 'Components/SmartLink';
import UserMenu from 'Features/UserMenu';
import { RedfastZoneByPathname } from 'Components/RedfastZone';
import { HTMLParser } from 'Utils/Parser';
import { classCombine } from 'Utils/Utils';
import { isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { LinkSubmitButton } from 'Components/Fields/Buttons';
import { VA_QUERY_PARAM } from 'Utils/Constants';
import { isFreeTrial } from 'Utils/Plans';
import { useStore } from 'Hooks/useStore';

const getHeaderRouteStateByPathname = (pathname = '') => ({
  isPurchaseFunnelRoute: /signup|signin|shipping|payment/g.test(pathname) && !/signup-confirmation/g.test(pathname),
  isGiftingRoute: /review-and-redeem|redeem-confirmation/g.test(pathname),
  isPromoBannerRoute: /signup-plans|shipping|payment/g.test(pathname),
  isNoSignInButtonRoute: /reset-by-email|mvpd|siriusxm-bango|verizon-bango/g.test(pathname),
  isNoSignUpButtonRoute: /signup-plans/g.test(pathname),
});

//Per sign up flow redesign experiment. If becomes permanent, add to isNoSignUpButtonRoute above
const noSignUpButtonPerRedesign = (pathname = '') => isDefaultPlanFlow() && /signup-account/g.test(pathname);

export const trackGlobalClick = (itemTitle, props = {}) => {
  trackEvent(TRACKING_ENUMS.EVENT_GLOBAL_NAV_CLICK, {
    page_item_title: itemTitle,
    ...props,
  });
};

const trackUpgradeClick = (text) => {
  trackEvent(TRACKING_ENUMS.EVENT_UPGRADE_CLICKED, {
    page_item_title: text,
    page_action_location: 'global nav',
  });
  trackEvent(TRACKING_ENUMS.EVENT_PURCHASE_CTA_CLICKED, {
    page_button_text: text,
    page_action_location: 'global nav',
    page_item_title: 'join fox nation',
  });
};

export function HeaderComponent() {
  // state
  const [showPromoBanner, setPromoBanner] = useState(false);
  const [showSignUpButton, setSignUpButton] = useState(false);
  const { scrolledClassName } = useScroll({ className: 'headerScrolled' });
  const { pathname = '', search = '' } = useLocation();
  //need to check entire search since the query param can be in a return_url query param
  const isVeteransAdv = !!search.includes(`?${VA_QUERY_PARAM}=`);

  const user = useStore('user');
  const purchaseMode = useStore('purchaseMode');
  const locale = useStore('appConfig.strings');
  const defaultConfig = useStore('appConfig.defaultConfig');
  const routes = useStore('appConfig.routes');
  const pageMeta = useStore('appConfig.pageMeta');
  const pageLayout = useStore('pageLayout');
  const secondScreen = useStore('secondScreen');
  const bango = useStore('integrations.bango');
  const allPlans = useStore('plans.allPlans');

  const { device, isDeviceActivation } = secondScreen;
  const { requirePlanSelection = true } = purchaseMode;
  const { showUserMenuOnPurchaseFlow, rootDomain, showHeaderSignUp } = defaultConfig;
  const isAuthenticated = user.isAuthenticated || user.hasMvpd;
  const isBangoFlow = !!bango.subscriptionId && !user.hasEntitlements;

  const { isPurchaseFunnelRoute, isGiftingRoute, isPromoBannerRoute, isNoSignInButtonRoute, isNoSignUpButtonRoute } =
    useMemo(() => getHeaderRouteStateByPathname(pathname), [pathname]);

  const hasNoSignUpButtonPerRedesign = useMemo(() => noSignUpButtonPerRedesign(pathname), [pathname]);

  // sign up button
  useEffect(() => {
    const mayShowSignUpButton =
      !isAuthenticated &&
      !isGiftingRoute &&
      !isDeviceActivation &&
      !isNoSignInButtonRoute &&
      !isBangoFlow &&
      !isVeteransAdv &&
      !hasNoSignUpButtonPerRedesign &&
      !isNoSignUpButtonRoute &&
      !!allPlans.length;
    const showSignUpButton = showHeaderSignUp && requirePlanSelection && mayShowSignUpButton;
    setSignUpButton(showSignUpButton);
  }, [
    isAuthenticated,
    showHeaderSignUp,
    requirePlanSelection,
    isGiftingRoute,
    isDeviceActivation,
    isNoSignInButtonRoute,
    isBangoFlow,
    isNoSignUpButtonRoute,
    hasNoSignUpButtonPerRedesign,
    isVeteransAdv,
    allPlans.length,
  ]);

  // promo banner
  useEffect(() => {
    const showPromoBanner = locale.headerPromoBanner && isPromoBannerRoute;
    setPromoBanner(showPromoBanner);
  }, [pageLayout, locale, isPromoBannerRoute]);

  // headerClass
  const headerClass = useMemo(() => {
    const promoBannerClass = showPromoBanner ? 'promoBanner' : '';
    return [scrolledClassName, promoBannerClass].filter(Boolean).join(' ');
  }, [scrolledClassName, showPromoBanner]);

  const signUpButtonText = useMemo(() => {
    const hasPlanWithFreeTrial = allPlans.find((p) => isFreeTrial(p));

    return hasPlanWithFreeTrial ? 'Start Your Free Trial' : 'Get Started';
  }, [allPlans]);

  const isSignUp = pathname.includes('/account/signup-plans');
  const variantTitle = 'Choose a Plan';

  const trialRoute = useMemo(() => {
    return isDefaultPlanFlow() ? routes.signupCreateAccount : routes.signupPlans;
  }, [routes]);

  const isSheerVerification = pathname.includes(routes.sheerIdVerification);

  return (
    <Fragment>
      <header className={classCombine('globalHeader', headerClass)}>
        <div className="globalHeaderCenter">
          <div className="headerSideLeft">
            {!isBangoFlow && (
              <>
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogo" width={92} height={87} />}
                  className="headerLogoLink hasVariant"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogoVariant" isVariant width={112} height={37} />}
                  className="headerLogoVariantLink"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
              </>
            )}
            {isBangoFlow && (
              <>
                <div className="headerLogoLink hasVariant">
                  <Logo className="headerLogo" width={92} height={87} />
                </div>
                <div className="headerLogoVariantLink">
                  <Logo className="headerLogoVariant" isVariant width={112} height={37} />
                </div>
              </>
            )}
          </div>
          <div className="headerCenter"></div>
          <div className="headerSideRight">
            {showSignUpButton && (
              <LinkSubmitButton
                className="headerSignupButton"
                modernStyle={true}
                inline={true}
                path={{ pathname: trialRoute, state: { page_source: 'global nav' } }}
                onClick={() => trackUpgradeClick(signUpButtonText)}
                text={signUpButtonText}
              />
            )}
            {pathname !== routes.signin &&
              (!isSheerVerification || (!isBangoFlow && isSheerVerification)) &&
              showUserMenuOnPurchaseFlow &&
              !isDeviceActivation &&
              !isNoSignInButtonRoute &&
              !(isPurchaseFunnelRoute && isBangoFlow) && (
                <UserMenu
                  segmentProps={{ page_start_source: 'global nav' }}
                  pathname={pathname}
                  locationSearch={search}
                />
              )}
            {isSignUp && !isAuthenticated && (
              <div className="variantTitleContainer">
                <h1 className="variantTitle">{variantTitle}</h1>
              </div>
            )}
            {isDeviceActivation && (
              <div className="secondScreen">
                Activate
                {device && (
                  <img
                    className="device"
                    src={`/account/static/images/deviceIcons/secondScreen/${device}.png`}
                    alt="Device"
                  />
                )}
              </div>
            )}
            {pageMeta.chatURL && (
              <div className="headerFaq">
                <SmartLink linkUrl={pageMeta.chatURL} text="faq" className="headerFaqBtn" target="_blank" reload />
              </div>
            )}
          </div>
        </div>
        {showPromoBanner && <div className="globalHeaderPromoBanner">{HTMLParser(locale.headerPromoBanner)}</div>}
      </header>
      <RedfastZoneByPathname />
    </Fragment>
  );
}
const Header = HeaderComponent;
export default Header;
